export const LIBRARY_NAME = "winlet";

export type WindowState = "normal" | "minimized" | "maximized";
/**
 * ウィンドウの仮想化レベル
 * - `none`: 仮想化されていない状態
 * - `frozen`: ウィンドウのコンテンツは保持されるが、インタラクションは停止
 * - `suspended`: ウィンドウのコンテンツはDOMから非表示にされ、描画負荷を削減
 * - `unloaded`: ウィンドウのコンテンツはメモリから解放され、リソースを最大限制御
 */
export type VirtualizationLevel = "none" | "frozen" | "suspended" | "unloaded";

/**
 * アニメーションの基点
 * - string: DOMセレクタ
 * - HTMLElement: DOM要素
 * - MouseEvent: マウスイベント
 * - object: 座標 { x: number; y: number }
 */
export type AnimationOrigin = string | HTMLElement | MouseEvent | { x: number; y: number };

/**
 * アニメーションのオプション
 */
export interface AnimationOptions {
	origin?: AnimationOrigin;
}

export interface WindowContentOptions {
	/**
	 * 直接埋め込むHTML
	 */
	html?: string;
	/**
	 * iframe設定
	 */
	iframe?: {
		src?: HTMLIFrameElement["src"];
		srcdoc?: HTMLIFrameElement["srcdoc"];
		allow?: HTMLIFrameElement["allow"];
		referrerPolicy?: HTMLIFrameElement["referrerPolicy"];
		loading?: HTMLIFrameElement["loading"];
		sandbox?: string[] | string;
		/**
		 * srcdocで生成されるiframe内にWinLetライブラリ自身を読み込むかどうか。
		 * trueにする場合、GlobalConfigOptionsでlibraryPathを指定する必要があります。
		 */
		loadWinLet?: boolean;
	};
	/**
	 * HTMLテンプレートのselector
	 */
	template?: string;
}

/**
 * 分割ビューのペイン定義
 */
export interface SplitViewPane {
	id: string;
	content: WindowContentOptions;
	size?: string; // "50%", "200px" など
	minSize?: string;
	resizable?: boolean;
}

/**
 * 分割ビューのレイアウト定義
 */
export interface SplitViewOptions {
	direction: "horizontal" | "vertical";
	panes: SplitViewPane[];
}

/**
 * ステータスバーのオプション
 */
export interface StatusBarOptions {
	/**
	 * ステータスバーを表示するかどうか
	 * @default false
	 */
	enabled?: boolean;
	/**
	 * 初期テキスト
	 */
	text?: string;
	/**
	 * HTMLを許可するか
	 * @default false
	 */
	allowHTML?: boolean;
}

/**
 * ウィンドウ内検索機能のオプション
 */
export interface SearchOptions {
	/**
	 * 検索UIを有効にするか
	 * @default true
	 */
	enabled?: boolean;
	/**
	 * 検索時に大文字小文字を区別するか
	 * @default false
	 */
	caseSensitive?: boolean;
	/**
	 * 「大文字/小文字の区別」ボタンを表示するか
	 * @default true
	 */
	showCaseSensitiveButton?: boolean;
	/**
	 * 「正規表現」ボタンを表示するか
	 * @default true
	 */
	showRegexButton?: boolean;
	/**
	 * 「単語単位」ボタンを表示するか
	 * @default true
	 */
	showWholeWordButton?: boolean;
	/**
	 * 検索対象のセレクタ
	 * 指定しない場合はウィンドウのコンテンツ全体
	 */
	targetSelector?: string;
}

export interface ListenerOptions {
	once?: boolean;
}

export interface WindowEventMap {
	open: (win: IWindow) => void;
	close: (win: IWindow) => void;
	"before-close": (win: IWindow) => boolean | void;
	focus: (win: IWindow) => void;
	blur: (win: IWindow) => void;
	"move-start": (win: IWindow) => void;
	"move-end": (win: IWindow) => void;
	move: (win: IWindow) => void;
	"resize-start": (win: IWindow) => void;
	"resize-end": (win: IWindow) => void;
	resize: (win: IWindow) => void;
	reload: (win: IWindow) => boolean | void;
	[key: string]: (...args: any[]) => any;
}

export interface GlobalEventMap {
	"window-created": (win: IWindow) => void;
	"window-destroyed": (win: IWindow) => void;
	[key: string]: (...args: any[]) => any;
}

// IWindowインターフェースを先に定義することで循環参照を回避
export interface IWindow {
	readonly id: string;
	options: Required<WindowOptions>;
	el: HTMLElement;
	state: WindowState;

	on<K extends keyof WindowEventMap>(eventName: K, listener: WindowEventMap[K], options?: ListenerOptions): void;
	off<K extends keyof WindowEventMap>(eventName: K, listener: WindowEventMap[K]): void;
	emit<K extends keyof WindowEventMap>(eventName: K, ...args: Parameters<WindowEventMap[K]>): ReturnType<WindowEventMap[K]>[] | undefined;

	// --- Public API ---
	/**
	 * ウィンドウを閉じます。
	 */
	close(): void;
	/**
	 * ウィンドウを最小化します。
	 * @param options - アニメーションのオプション
	 */
	minimize(options?: AnimationOptions): void;
	/**
	 * ウィンドウを最大化/元に戻します。
	 */
	toggleMaximize(): void;
	/**
	 * ウィンドウを最大化します。
	 */
	maximize(): void;
	/**
	 * ウィンドウを元に戻します。
	 * @param options - アニメーションのオプション
	 */
	restore(options?: AnimationOptions): void;
	/**
	 * ウィンドウをフォーカスします。
	 */
	focus(): void;
	/**
	 * ウィンドウからフォーカスを外します。
	 */
	blur(): void;
	/**
	 * 注意を引くためにウィンドウを揺らします。
	 */
	shake(): void;
	/**
	 * ウィンドウの内容をキャプチャして、画像データとして取得します。
	 * @param mode - "window" (デフォルト) または "content"
	 * @returns PNG形式のデータURLを含むPromise
	 * @see https://github.com/niklasvh/html2canvas
	 */
	capture(mode?: "window" | "content"): Promise<string>;
	/**
	 * ウィンドウのコンテンツを印刷します。
	 */
	print(): void;
	getTitle(): string;
	setTitle(title: string): void;
	setIcon(icon: string | null): void;
	getPosition(): { x: number; y: number };
	setPosition(x: number | "center" | "left" | "right" | "auto", y: number | "center" | "top" | "bottom" | "auto"): void;
	getSize(): { width: number; height: number };
	setSize(width: number | string, height: number | string): void;
	/**
	 * ウィンドウの不透明度を設定します。
	 * @param opacity 0.0 (透明) から 1.0 (不透明) の間の値
	 */
	setOpacity(opacity: number): void;
	/**
	 * ウィンドウをリロードします。
	 */
	reload(): void;

	/**
	 * ウィンドウのコンテンツ要素（またはiframe要素）を取得します。
	 * @returns コンテンツを内包するHTMLElement、またはiframeウィンドウの場合はHTMLIFrameElement
	 */
	getContent(): HTMLElement | HTMLIFrameElement;

	/**
	 * ステータスバーのテキストを更新します。
	 * @param text - 表示するテキストまたはHTML
	 */
	setStatusBarText(text: string): void;

	/**
	 * ウィンドウ内検索UIを開きます。
	 */
	openSearch(): void;

	/**
	 * ウィンドウ内検索UIを閉じます。
	 */
	closeSearch(): void;

	/**
	 * このウィンドウの内部に新しいウィンドウを作成します。
	 * @param options - 新しいウィンドウのオプション
	 * @returns 作成されたIWindowインスタンス
	 */
	createWindow(options?: WindowOptions): IWindow;

	/**
	 * このウィンドウの内部に新しいポップアップを作成します。
	 * @param options - 新しいポップアップのオプション
	 * @returns 作成されたIWindowインスタンス
	 */
	createPopup(options: PopupOptions): IWindow;
	/**
	 * このウィンドウの内部に新しいポップアップを作成します。
	 * @param options - 新しいポップアップのオプション
	 * @returns Promiseオブジェクト
	 */
	createAsyncPopup(options: PopupOptions): Promise<PopupResult>;

	/**
	 * ウィンドウのオプションを動的に変更します。
	 *
	 * (初期化時に使用されるオプションは変更されず、警告なども出力されません。)
	 *
	 * @param options - 変更したいオプション
	 */
	setOptions(options: Partial<WindowOptions>): void;
}

export interface MenuItem {
	name?: string;
	action?: (win: IWindow) => void;
	items?: MenuItem[];
	separator?: boolean;
	shortcut?: string;
}

export interface TabItem {
	title: string;
	content: WindowContentOptions;
}

export interface ContextMenuItem {
	name?: string;
	action?: (win: IWindow) => void;
	separator?: boolean;
}

export interface CustomControlButton {
	/**
	 * ボタンを識別するための一意の名前
	 */
	name: string;
	/**
	 * ボタンのHTMLコンテンツ (例: '<i class="fa fa-question"></i>')
	 */
	html: string;
	/**
	 * ボタンのツールチップテキスト
	 */
	title?: string;
	/**
	 * ボタンクリック時のアクション
	 */
	action: (win: IWindow) => void;
}

export interface WindowOptions {
	/**
	 * ウィンドウのID
	 */
	id?: string | null;
	/**
	 * 既に同名のウィンドウが存在する場合の動作
	 *
	 * focus (デフォルト): 既存のウィンドウを復元し、フォーカスします。
	 * prevent: 新しいウィンドウを作成せず、処理を中断します。
	 * create: IDの重複を無視して、常に新しいウィンドウを作成します。
	 */
	ifExists?: "focus" | "prevent" | "create";
	/**
	 * ウィンドウのタイトル
	 */
	title?: string;
	/**
	 * ウィンドウのアイコン
	 *
	 * url / font-awesomeのクラス名の指定可能
	 */
	icon?: string | null;
	/**
	 * ウィンドウのX座標
	 */
	x?: number | "center" | "left" | "right" | "auto";
	/**
	 * ウィンドウのY座標
	 */
	y?: number | "center" | "top" | "bottom" | "auto";
	/**
	 * ウィンドウのサイズX
	 */
	width?: number;
	/**
	 * ウィンドウのサイズY
	 */
	height?: number;
	/**
	 * ウィンドウの最小サイズX
	 */
	minWidth?: number;
	/**
	 * ウィンドウの最小サイズY
	 */
	minHeight?: number;
	/**
	 * ウィンドウが仮想化の対象となるかどうか。
	 * `enableVirtualization` がtrueの場合にのみ有効です。
	 * @default true
	 */
	virtualizable?: boolean;
	/**
	 * 拡大・縮小アニメーションの基点を指定します。
	 * - セレクタ文字列 (e.g., '#my-button')
	 * - HTMLElement
	 * - MouseEvent
	 * - 座標オブジェクト {x: number, y: number}
	 */
	animationOrigin?: AnimationOrigin | null;
	/**
	 * ウィンドウのオプション
	 */
	windowOptions?: {
		/**
		 * 水平方向のリサイズ
		 */
		resizableX?: boolean;
		/**
		 * 垂直方向のリサイズ
		 */
		resizableY?: boolean;
		/**
		 * ウィンドウの移動
		 */
		movable?: boolean;
		/**
		 * ウィンドウを閉じるボタンの表示
		 */
		closable?: boolean;
		/**
		 * ウィンドウを最小化するボタンの表示
		 */
		minimizable?: boolean;
		/**
		 * ウィンドウを最大化するボタンの表示
		 */
		maximizable?: boolean;
		/**
		 * タイトルバーのダブルクリックで最大化
		 */
		maximizableOnDblClick?: boolean;
		/**
		 * ウィンドウを常に最前面に表示するかどうか
		 */
		alwaysOnTop?: boolean;
		/**
		 * ドラッグ・リサイズ時にゴーストウィンドウ（輪郭）を表示するか
		 */
		useGhostWindow?: boolean;
		/**
		 * 作成時にウィンドウにフォーカスを当てるか
		 */
		focus?: boolean;
		/**
		 * ウィンドウの不透明度 (0.0 - 1.0)
		 */
		opacity?: number;
		/**
		 * ウィンドウをモーダルとして扱うか
		 * trueの場合、このウィンドウ以外は操作できなくなります。
		 */
		modal?: boolean;
	};
	/**
	 * ウィンドウのコンテンツ
	 */
	content?: WindowContentOptions;
	/**
	 * 分割ビューの定義。
	 * `content`オプションと同時に指定された場合、こちらが優先されます。
	 */
	splitView?: SplitViewOptions;
	/**
	 * ステータスバーのオプション
	 */
	statusBar?: StatusBarOptions;
	/**
	 * ウィンドウ内検索のオプション
	 */
	search?: SearchOptions;
	/**
	 * メニュー
	 */
	menu?: MenuItem[];
	/**
	 * メニューの表示形式
	 * - default: 通常のメニューバー
	 * - merged: タイトルバーに統合
	 */
	menuStyle?: "default" | "merged";
	/**
	 * タブ
	 */
	tabs?: TabItem[];
	/**
	 * タブの表示形式
	 * - default: 通常のタブバー
	 * - merged: タイトルバーに統合されたChrome風タブ
	 */
	tabStyle?: "default" | "merged";
	tabOptions?: {
		/**
		 * タブの入れ替え
		 */
		reorderable?: boolean;
		/**
		 * タブを閉じるボタン
		 */
		closable?: boolean;
		/**
		 * タブ追加ボタン
		 */
		addable?: boolean;
		/**
		 * タブをドラッグして新しいウィンドウとして分離できるようにするか
		 */
		detachable?: boolean;
		/**
		 * タブを別の既存のウィンドウにマージすることができます。
		 */
		mergeable?: boolean;
		/**
		 * このウィンドウが他のウィンドウからマージされたタブを受け入れることができます。
		 */
		allowIncomingMerge?: boolean;
		/**
		 * 追加ボタン押下時の処理
		 */
		onAdd?: (win: IWindow) => TabItem;
		/**
		 * マージが許可されているかどうかを動的に決定するコールバック関数。
		 * @param sourceWindow タブの元ウィンドウ
		 * @param targetWindow ドロップされたタブのウィンドウ
		 * @returns true:マージ許可 false:マージ禁止
		 */
		onMergeAttempt?: (sourceWindow: IWindow, targetWindow: IWindow) => boolean;
	};
	/**
	 * タスクバーアイテムの表示設定
	 */
	taskbarOptions?: {
		/**
		 * trueにすると、タスクバーにタイトルではなくアイコンを表示します。
		 * アイコンが設定されている必要があります。
		 */
		showIconOnly?: boolean;
	};
	/**
	 * 右クリックメニュー
	 */
	contextMenu?: ContextMenuItem[];
	/**
	 * タイトルバーに追加するカスタムコントロールボタン
	 */
	customControls?: CustomControlButton[];
	/**
	 * ショートカットキーを有効化
	 */
	enableShortcuts?: boolean;
	/**
	 * コントロールの位置
	 */
	controlsPosition?: "left" | "right";
	/**
	 * iframeや非同期コンテンツの読み込み中にローディングインジケーターを表示するか
	 */
	showLoadingIndicator?: boolean;
	/**
	 * コンテンツの読み込みを初回フォーカス時まで遅延させるか
	 * @default false
	 */
	lazyLoad?: boolean;
	/**
	 * moveイベントの発火を無効にするか
	 * @default false
	 */
	disableMoveEvent?: boolean;
	/**
	 * resizeイベントの発火を無効にするか
	 * @default false
	 */
	disableResizeEvent?: boolean;
	/**
	 * このウィンドウに適用する仮想化戦略
	 * 仮想化からの復帰モード
	 * 'auto': ウィンドウが可視になった際に自動で復帰 (デフォルト)
	 * 'manual': ユーザーのアクション（クリック、フォーカス、更新ボタン）で復帰
	 */
	virtualizationRestoreMode?: "auto" | "manual";
	/**
	 * 手動復帰モードの際に更新ボタンを表示するか
	 * @default true
	 */
	showVirtualizationRefreshButton?: boolean;
	/**
	 * ウィンドウオープン時
	 */
	onOpen?: (win: IWindow) => void;
	/**
	 * ウィンドウクローズ時
	 */
	onClose?: (win: IWindow) => void;
	/**
	 * ウィンドウが閉じる前に呼び出され、falseを返すとクローズをキャンセルします。
	 */
	onBeforeClose?: (win: IWindow) => boolean | void;
	/**
	 * ウィンドウフォーカス時
	 */
	onFocus?: (win: IWindow) => void;
	/**
	 * ウィンドウブラー時
	 */
	onBlur?: (win: IWindow) => void;
	/**
	 * ウィンドウリサイズ時
	 */
	onResize?: (win: IWindow) => void;
	/**
	 * ウィンドウの移動開始時
	 */
	onMoveStart?: (win: IWindow) => void;
	/**
	 * ウィンドウの移動終了時
	 */
	onMoveEnd?: (win: IWindow) => void;
	/**
	 * ウィンドウのリサイズ開始時
	 */
	onResizeStart?: (win: IWindow) => void;
	/**
	 * ウィンドウのリサイズ終了時
	 */
	onResizeEnd?: (win: IWindow) => void;
	/**
	 * ウィンドウ移動時
	 */
	onMove?: (win: IWindow) => void;
	/**
	 * ウィンドウリロード時
	 *
	 * デフォルトのリロード動作（iframeの再読み込みやhtmlの再描画）をキャンセルする場合は `false` を返します。
	 */
	onReload?: (win: IWindow) => boolean | void;

	// 内部利用フラグ
	_isPopup?: boolean;
	/**
	 * 親ウィンドウのインスタンス (内部利用)
	 */
	_parent?: IWindow | null;
	/**
	 * タスクバーのアイテム要素 (内部利用)
	 */
	_taskbarItem?: HTMLElement | null;
}

// --- Popup ---
export const TIMEOUT_RESULT = Symbol("timeout");
export const CLOSE_BUTTON_RESULT = Symbol("close");

export type PopupResult = string | number | symbol | null;

export interface PopupButton {
	text: string;
	value: PopupResult;
}

export type PopupButtonPreset = "Ok" | "OkCancel" | "Yes" | "YesNo" | "YesNoCancel" | "Retry" | "RetryCancel" | "AbortRetryIgnore";

export interface PopupOptions {
	message: string;
	title?: string;
	icon?: string | null;
	buttons?: PopupButton[] | PopupButtonPreset;
	timeout?: number;
	autoWidth?: boolean;
	focus?: boolean;
	alwaysOnTop?: boolean;
	modal?: boolean;

	/**
	 * ウィンドウクローズ時
	 */
	onClose?: (result: PopupResult) => void;
	/**
	 * ウィンドウフォーカス時
	 */
	onFocus?: (win: IWindow) => void;
	/**
	 * ウィンドウブラー時
	 */
	onBlur?: (win: IWindow) => void;
}

export interface TaskbarOptions {
	/**
	 * タスクバーの表示位置
	 * @default 'bottom'
	 */
	position?: "top" | "bottom" | "left" | "right";
}

export interface GlobalConfigOptions {
	windowSwitchShortcut?: string | null; // 例: "Ctrl+`"
	/**
	 * ウィンドウマネージャを内包する親要素。
	 * セレクタ文字列またはHTMLElementを指定します。
	 * 未指定の場合はbody直下に配置されます。
	 */
	container?: string | HTMLElement;
	/**
	 * `srcdoc` iframeでWinLetを自己読込する場合のライブラリのパス。
	 */
	libraryPath?: string;
	/**
	 * ウィンドウ操作時のアニメーションを有効にするか
	 * @default true
	 */
	enableAnimations?: boolean;
	/**
	 * 最小化・復元時にタスクバーのアイコンからアニメーションさせるか
	 * @default true
	 */
	animateFromTaskbar?: boolean;
	/**
	 * タスクバーを表示するか
	 * @default false
	 */
	enableTaskbar?: boolean;
	/**
	 * タスクバーの設定
	 */
	taskbar?: TaskbarOptions;
	/**
	 * モーダルウィンドウのフォーカストラップを有効にするか
	 * @default true
	 */
	enableFocusTrapping?: boolean;
	/**
	 * ウィンドウ仮想化を有効にするか。
	 * 大量のウィンドウを扱う際のパフォーマンスを向上させます。
	 * @default true
	 */
	enableVirtualization?: boolean;
	/**
	 * 仮想化されたウィンドウをタスクバーで視覚的に示すか
	 * @default true
	 */
	indicateVirtualizationInTaskbar?: boolean;
	/**
	 * グローバルな仮想化戦略
	 * グローバルな仮想化からの復帰モード
	 * 'auto': ウィンドウが可視になった際に自動で復帰 (デフォルト)
	 * 'manual': ユーザーのアクション（クリック、フォーカス、更新ボタン）で復帰
	 */
	virtualizationRestoreMode?: "auto" | "manual";
	/**
	 * 仮想化がアクティブになる最小ウィンドウ数。
	 * この数を超えると、非表示のウィンドウの仮想化が開始されます。
	 * @default 5
	 */
	virtualizationThreshold?: number;
	/**
	 * 最小化されたウィンドウを仮想化するまでの遅延時間（ミリ秒）。
	 * `enableVirtualization` がtrueの場合にのみ有効です。
	 * @default 10000
	 */
	virtualizationDelay?: number;
	/**
	 * イベントのスロットリング設定 (ミリ秒)
	 */
	eventThrottling?: {
		moveDelay?: number;
		resizeDelay?: number;
	};
	/**
	 * 初期テーマ
	 * 'dark'などの登録済みテーマ名、またはThemeオブジェクトを指定
	 * @default 'default'
	 */
	theme?: string | Theme;
	/**
	 * Bootstrapのカラースキームを自動検出し、テーマを同期させるか
	 * @default true
	 */
	autoDetectBootstrapTheme?: boolean;
	/**
	 * デバッグモードを有効にするか。
	 * ウィンドウにID、座標、状態などのデバッグ情報をオーバーレイ表示します。
	 * @default false
	 */
	enableDebugMode?: boolean;
}

/**
 * テーマの定義
 */
export interface Theme {
	name: string;
	variables: Record<string, string>;
}

// WinLetの公開APIの型
export interface WinLetApi {
	init: (options?: GlobalConfigOptions) => void;
	on<K extends keyof GlobalEventMap>(eventName: K, listener: GlobalEventMap[K], options?: ListenerOptions): void;
	off<K extends keyof GlobalEventMap>(eventName: K, listener: GlobalEventMap[K]): void;
	emit<K extends keyof GlobalEventMap>(eventName: K, ...args: Parameters<GlobalEventMap[K]>): ReturnType<GlobalEventMap[K]>[] | undefined;
	/**
	 * ウィンドウを作成します。
	 * @param options - 新しいウィンドウのオプション
	 * @returns 作成されたIWindowインスタンス
	 */
	createWindow: (options?: WindowOptions) => IWindow;
	/**
	 * ポップアップウィンドウを作成します。
	 * @param options - 新しいポップアップのオプション
	 * @returns 作成されたIWindowインスタンス
	 */
	createPopup: (options: PopupOptions) => IWindow;
	/**
	 * ポップアップウィンドウを作成します。
	 * @param options - 新しいポップアップのオプション
	 * @returns Promiseオブジェクト
	 */
	createAsyncPopup(options: PopupOptions): Promise<PopupResult>;
	/**
	 * 指定されたIDのウィンドウインスタンスを取得します。
	 * @param id - 取得するウィンドウのID
	 */
	getWindow: (id: string) => IWindow | undefined;
	/**
	 * 指定されたDOM要素を内包するウィンドウインスタンスを取得します。
	 * 入れ子ウィンドウの場合、最も内側にある子ウィンドウを返します。
	 * @param element - 検索の起点となるDOM要素
	 * @returns 発見されたIWindowインスタンス、またはundefined
	 */
	getWindowFromElement: (element: HTMLElement) => IWindow | undefined;
	/**
	 * 現在アクティブなウィンドウを取得します。
	 */
	getActiveWindow: () => IWindow | null;
	/**
	 * タスクバーのアイテムを更新します。
	 * @param win - 更新するウィンドウ
	 * @param state - 更新する状態
	 */
	updateTaskbarItem: (win: IWindow, state: "minimized" | "restored" | "titleChanged" | "iconChanged" | "virtualized" | "unvirtualized") => void;
	/**
	 * ウィンドウのデフォルトの設定を変更します。
	 * @param options - 新しいウィンドウのオプション
	 */
	setDefaultConfig: (options: WindowOptions) => void;
	/**
	 * グローバル設定を変更します。
	 * @param options - 新しいウィンドウのオプション
	 */
	setGlobalConfig: (options: GlobalConfigOptions) => void;
	/**
	 * 表示テーマを登録します。
	 * @param theme - 登録するテーマ
	 */
	registerTheme: (theme: Theme) => void;
	/**
	 * 登録済みのテーマ名の配列を取得します。
	 * @returns 登録済みのテーマ名の配列
	 */
	getRegisteredThemes: () => string[];
	/**
	 * 表示テーマを変更します。
	 * @param theme - 登録済みのテーマ名（'default', 'dark'など）またはThemeオブジェクト
	 */
	setTheme: (theme: string | Theme) => void;
	/**
	 * 現在の表示テーマを取得します。
	 * @returns 現在の表示テーマ
	 */
	getTheme: () => Theme | null;
	/**
	 * WinLetのバージョンを取得します。
	 */
	get version(): string;
	POPUP_TIMEOUT_RESULT: typeof TIMEOUT_RESULT;
	POPUP_CLOSE_BUTTON_RESULT: typeof CLOSE_BUTTON_RESULT;
}

// グローバルスコープのWindowにWinLetプロパティを追加
declare global {
	interface Window {
		WinLet: WinLetApi;

		// 外部ライブラリを追加
		html2canvas?: (element: HTMLElement, options?: any) => Promise<HTMLCanvasElement>;
	}
}
